import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Reveal } from '@/components/motion';
import { MagnifyingGlass, MapPin, NavigationArrow, XCircle, Clock } from '@phosphor-icons/react';
import { public_api } from '@/lib/api';

const STATUS_STYLE = {
  pending: 'bg-neutral-200 text-neutral-700',
  confirmed: 'bg-blue-100 text-blue-700',
  assigned: 'bg-indigo-100 text-indigo-700',
  en_route: 'bg-blue-100 text-blue-700',
  arrived: 'bg-cyan-100 text-cyan-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
  no_show: 'bg-red-100 text-red-600',
};

const CANCELLABLE = ['pending', 'confirmed', 'assigned'];

export default function MyBookings() {
  const [email, setEmail] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('email'); // email → code → results
  const [code, setCode] = useState('');
  const [token, setToken] = useState(null);

  const requestCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await public_api.bookingRequestAccess(email);
      toast.success('If bookings exist for this email, a 6-digit code has been sent to it.');
      setStage('code');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send verification code.');
    } finally { setBusy(false); }
  };

  const verifyAndLookup = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const res = await public_api.bookingVerifyAccess(email, code);
      setToken(res.token);
      const list = await public_api.bookingLookup(email, bookingId || null, res.token);
      setItems(list.items || []);
      setStage('results');
      if (!list.items?.length) toast.info('No bookings found for that email.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired code.');
    } finally { setBusy(false); }
  };

  const refresh = async () => {
    try {
      const list = await public_api.bookingLookup(email, bookingId || null, token);
      setItems(list.items || []);
    } catch { setStage('email'); setToken(null); toast.error('Session expired — please verify again.'); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this booking? This cannot be undone online.')) return;
    try {
      await public_api.bookingCancel(email, id, token);
      toast.success('Booking cancelled. Our team has been notified.');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel this booking.');
    }
  };

  return (
    <section className="pt-16 md:pt-24 pb-24" data-testid="my-bookings-page">
      <div className="container-x max-w-3xl">
        <Reveal>
          <div className="overline mb-6">Manage my transport</div>
          <h1 className="h-display text-balance">Track, manage or cancel your bookings.</h1>
          <p className="body-lg mt-6 text-pretty">
            Enter the email address you booked with. To reschedule a trip, cancel it below and rebook, or call our 24/7 team.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          {stage === 'email' && (
            <form onSubmit={requestCode} className="mt-10 p-6 rounded-3xl bg-neutral-50 border border-neutral-200 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4" data-testid="my-bookings-form">
              <div>
                <label className="label-base">Email</label>
                <input required type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="my-bookings-email" className="input-base" placeholder="you@email.com.au" />
              </div>
              <div>
                <label className="label-base">Booking ID (optional)</label>
                <input value={bookingId} onChange={e => setBookingId(e.target.value)} data-testid="my-bookings-id" className="input-base" placeholder="Paste from your confirmation" />
              </div>
              <div className="flex items-end">
                <button type="submit" disabled={busy} data-testid="my-bookings-submit" className="btn-primary !py-3 w-full md:w-auto">
                  <MagnifyingGlass size={16} weight="bold" /> {busy ? 'Sending…' : 'Send access code'}
                </button>
              </div>
              <p className="md:col-span-3 text-xs text-ink-muted">For your privacy, we'll email a 6-digit verification code to confirm it's really you before showing any booking details.</p>
            </form>
          )}
          {stage === 'code' && (
            <form onSubmit={verifyAndLookup} className="mt-10 p-6 rounded-3xl bg-neutral-50 border border-neutral-200 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4" data-testid="my-bookings-code-form">
              <div>
                <label className="label-base">Enter the 6-digit code sent to {email}</label>
                <input required inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} data-testid="my-bookings-code" className="input-base tracking-[0.5em] font-mono text-xl" placeholder="000000" />
              </div>
              <div className="flex items-end gap-3">
                <button type="submit" disabled={busy || code.length !== 6} data-testid="my-bookings-verify" className="btn-primary !py-3">
                  {busy ? 'Verifying…' : 'Verify & view bookings'}
                </button>
                <button type="button" onClick={() => { setStage('email'); setCode(''); }} data-testid="my-bookings-back" className="text-sm text-ink-muted hover:text-ink">Change email</button>
              </div>
            </form>
          )}
        </Reveal>

        {stage === 'results' && items !== null && (
          <div className="mt-8 space-y-4" data-testid="my-bookings-list">
            {items.length === 0 && (
              <div className="p-8 rounded-2xl bg-neutral-50 border border-neutral-200 text-center text-ink-muted">
                No bookings found. <Link to="/book" className="text-brand font-semibold">Book transport →</Link>
              </div>
            )}
            {items.map(b => (
              <div key={b.id} className="p-6 rounded-2xl bg-white border border-neutral-200" data-testid={`my-booking-${b.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[b.status] || 'bg-neutral-200'}`}>
                    {b.status.replace('_', ' ')}
                  </span>
                  <span className="text-sm font-semibold text-ink flex items-center gap-1.5">
                    <Clock size={14} /> {b.pickup_date} {b.pickup_time}
                  </span>
                </div>
                <div className="font-display font-bold text-lg text-ink">{b.service_type}</div>
                <div className="mt-2 space-y-1.5 text-sm text-ink-soft">
                  <div className="flex items-start gap-2"><MapPin size={15} className="text-brand mt-0.5 shrink-0" /> {b.pickup_address}</div>
                  <div className="flex items-start gap-2"><NavigationArrow size={15} className="text-ink-muted mt-0.5 shrink-0" /> {b.dropoff_address}</div>
                </div>
                {(b.driver_assigned || b.vehicle_assigned) && (
                  <div className="mt-3 text-xs text-ink-muted">
                    {b.driver_assigned && <>Driver: <span className="font-semibold text-ink">{b.driver_assigned}</span></>}
                    {b.vehicle_assigned && <> · Vehicle: <span className="font-semibold text-ink">{b.vehicle_assigned}</span></>}
                  </div>
                )}
                <div className="mt-2 text-[11px] text-ink-muted font-mono">Ref: {b.id}</div>
                {CANCELLABLE.includes(b.status) && (
                  <button onClick={() => cancel(b.id)} data-testid={`my-booking-cancel-${b.id}`} className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-500 px-4 py-2 text-xs font-semibold hover:bg-red-50 transition">
                    <XCircle size={14} /> Cancel booking
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
